import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShieldCheck, MessageCircle, Moon, Sun } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { useTheme } from "@/contexts/ThemeContext";
import { NAV } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_bf956fe1-1570-4257-80a8-b41c4eebcfc0/artifacts/pp6j9f5w_LOGO.png";

const LINKS = [
  { to: "/", label: "Home", key: "linkHome" },
  { to: "/about", label: "About", key: "linkAbout" },
  { to: "/products", label: "Products", key: "linkProducts" },
  { to: "/brands", label: "Brands", key: "linkBrands" },
  { to: "/categories", label: "Categories", key: "linkCategories" },
  { to: "/gallery", label: "Gallery", key: "linkGallery" },
  { to: "/blogs", label: "Blogs", key: "linkBlogs" },
  { to: "/faq", label: "FAQ", key: "linkFaq" },
  { to: "/contact", label: "Contact", key: "linkContact" },
];

export default function Header() {
  const { settings } = useSite();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const headerBg = scrolled || !isHome ? "glass border-b border-border/60" : "bg-transparent";

  return (
    <>
      {settings.announcement_active && settings.announcement_text ? (
        <div className="w-full bg-navy text-gold py-2 text-center text-xs tracking-[0.18em] uppercase font-medium relative overflow-hidden" data-testid="announcement-bar">
          <div className="absolute inset-x-0 top-0 h-px flex">
            <span className="flex-1 bg-brand_blue" />
            <span className="flex-1 bg-gold" />
            <span className="flex-1 bg-brand_green" />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-px flex">
            <span className="flex-1 bg-brand_green" />
            <span className="flex-1 bg-gold" />
            <span className="flex-1 bg-brand_blue" />
          </div>
          <div className="flex items-center justify-center gap-3 px-4 flex-wrap">
            <span className="hidden sm:inline text-brand_blue">◆</span>
            <span>{settings.announcement_text}</span>
            <span className="hidden sm:inline text-brand_green">◆</span>
          </div>
        </div>
      ) : null}

      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`sticky top-0 z-50 w-full transition-colors ${headerBg}`}
      >
        <div className="mx-auto max-w-7xl px-4 md:px-8 h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 shrink-0">
            <img
              src={settings.logo || DEFAULT_LOGO}
              alt="Hermanos Industries"
              loading="eager"
              decoding="async"
              className="h-11 w-auto object-contain"
              onError={(e) => {
                if (e.currentTarget.src !== DEFAULT_LOGO) e.currentTarget.src = DEFAULT_LOGO;
              }}
            />
            <div className="hidden md:flex flex-col leading-tight">
              <span className="font-serif text-lg text-foreground">Hermanos</span>
              <span className="text-[0.6rem] tracking-[0.24em] uppercase text-gold font-semibold">Industries Pvt Ltd</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={NAV[l.key]}
                className={({ isActive }) =>
                  `text-[0.82rem] tracking-wider uppercase font-medium transition-colors ${
                    isActive ? "text-gold" : "text-foreground/70 hover:text-foreground"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${settings.phone_primary}`}
              className="hidden md:inline-flex items-center gap-2 text-xs tracking-wider text-foreground/80 hover:text-gold transition-colors"
              data-testid="header-phone"
            >
              <Phone size={14} /> {settings.phone_primary}
            </a>
            <button
              onClick={toggle}
              data-testid={NAV.themeToggle}
              className="hidden md:flex h-9 w-9 items-center justify-center rounded-sm border border-border/70 text-foreground/70 hover:text-gold hover:border-gold/60 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Link to="/request-quotation" className="hidden sm:block">
              <Button
                data-testid={NAV.requestQuote}
                className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs px-5 h-10"
              >
                Request Quote
              </Button>
            </Link>
            <Link
              to="/admin/login"
              className="hidden lg:flex h-9 w-9 items-center justify-center rounded-sm border border-border/70 text-foreground/70 hover:text-gold hover:border-gold/60 transition-colors"
              aria-label="Admin"
              data-testid={NAV.adminLogin}
            >
              <ShieldCheck size={16} />
            </Link>
            <button
              onClick={() => setMobileOpen((x) => !x)}
              data-testid={NAV.mobileMenuTrigger}
              className="lg:hidden h-10 w-10 rounded-sm border border-border/70 flex items-center justify-center text-foreground"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="lg:hidden fixed inset-x-0 top-20 z-40 glass border-b border-border/60 max-h-[80vh] overflow-y-auto"
            data-testid={NAV.mobileMenu}
          >
            <div className="px-4 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`m-${NAV[l.key]}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-sm text-sm tracking-wider uppercase ${
                      isActive ? "text-gold bg-secondary" : "text-foreground/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-4 grid gap-2">
                <Link to="/request-catalogue">
                  <Button className="w-full rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs h-11" data-testid={`m-${NAV.requestCatalogue}`}>
                    Request Catalogue
                  </Button>
                </Link>
                <Link to="/request-quotation">
                  <Button variant="outline" className="w-full rounded-sm border-gold/60 text-foreground tracking-wider uppercase text-xs h-11" data-testid={`m-${NAV.requestQuote}`}>
                    Request Quotation
                  </Button>
                </Link>
                <a href={`tel:${settings.phone_primary}`} className="flex items-center justify-center gap-2 h-11 rounded-sm border border-border text-foreground/80 text-xs tracking-wider uppercase">
                  <Phone size={14} /> {settings.phone_primary}
                </a>
                <a href={`https://wa.me/${settings.whatsapp?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 h-11 rounded-sm border border-emerald_brand/60 text-emerald-500 text-xs tracking-wider uppercase">
                  <MessageCircle size={14} /> WhatsApp Business
                </a>
                <Link to="/admin/login" className="flex items-center justify-center gap-2 h-11 rounded-sm border border-border text-foreground/70 text-xs tracking-wider uppercase" data-testid={`m-${NAV.adminLogin}`}>
                  <ShieldCheck size={14} /> Admin Login
                </Link>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
